/**
 * product-card-cart-sync.js
 *
 * Two jobs, both defensive:
 *
 * 1. Keeps every product card's quick add / stepper in step with the real
 *    cart. The cart drawer can change quantities without the collection page
 *    hearing about it, so cards would otherwise stay stale until a reload.
 *
 * 2. Repairs the quick add button's contents. product-card-v2.js sets the
 *    button's text directly (e.g. "Adding.."), which destroys the icon and
 *    label spans — after that the button is a bare text node and the cart
 *    symbol never comes back. This rebuilds it once the button is idle again.
 *
 *    The cleaner fix is in product-card-v2.js: write to
 *    .mnm-card__quickadd-label instead of the button itself. This file makes
 *    the card correct either way.
 *
 * Markup contract:
 *   .mnm-card__cartcontrol[data-variant-id]
 *     [data-add-btn][data-add-label]   quick add button
 *     [data-stepper]                   stepper wrapper, with an <input> inside
 */
(function () {
  'use strict';

  var CONTROL = '.mnm-card__cartcontrol[data-variant-id]';
  var ICON =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" ' +
    'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
    '<circle cx="9" cy="20" r="1.4" fill="currentColor" stroke="none"/>' +
    '<circle cx="18" cy="20" r="1.4" fill="currentColor" stroke="none"/>' +
    '<path d="M2 3h2.2l2.4 12.1a1.6 1.6 0 0 0 1.6 1.3h9.3a1.6 1.6 0 0 0 1.6-1.3L21 7H5"/>' +
    '</svg>';

  var inFlight = null;
  var queued = false;

  /* --- Button repair ------------------------------------------------------ */

  function isFlattened(btn) {
    if (!btn.querySelector('.mnm-card__quickadd-icon')) return true;
    if (!btn.querySelector('.mnm-card__quickadd-label')) return true;
    // Stray text written straight onto the button, alongside the spans.
    for (var i = 0; i < btn.childNodes.length; i++) {
      var node = btn.childNodes[i];
      if (node.nodeType === 3 && node.nodeValue.trim() !== '') return true;
    }
    return false;
  }

  function repairButton(btn) {
    if (!btn || btn.disabled) return; // mid-request: leave its feedback alone
    if (!isFlattened(btn)) return;

    var label = btn.getAttribute('data-add-label') || 'Add';
    var icon = document.createElement('span');
    icon.className = 'mnm-card__quickadd-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = ICON;

    var text = document.createElement('span');
    text.className = 'mnm-card__quickadd-label';
    text.textContent = label;

    btn.textContent = '';
    btn.appendChild(icon);
    btn.appendChild(text);
  }

  function watchButton(btn) {
    if (btn.__mnmWatched || !('MutationObserver' in window)) return;
    btn.__mnmWatched = true;

    var timer = null;
    var observer = new MutationObserver(function () {
      clearTimeout(timer);
      // Wait out the transient "Adding.." state before restoring the icon.
      timer = setTimeout(function () {
        repairButton(btn);
      }, 400);
    });
    observer.observe(btn, { childList: true, characterData: true, subtree: true });
  }

  function watchAllButtons() {
    var buttons = document.querySelectorAll(CONTROL + ' [data-add-btn]');
    for (var i = 0; i < buttons.length; i++) watchButton(buttons[i]);
  }

  /* --- Cart sync ---------------------------------------------------------- */

  function cartUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + '/cart.js';
  }

  function quantitiesFromCart(cart) {
    var map = Object.create(null);
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      map[String(items[i].variant_id)] = items[i].quantity;
    }
    return map;
  }

  function applyToCard(control, qty) {
    var addBtn = control.querySelector('[data-add-btn]');
    var stepper = control.querySelector('[data-stepper]');
    var input = stepper && stepper.querySelector('input');

    // Don't fight a stepper the shopper is actively typing in.
    if (input && document.activeElement === input) return;

    if (input && String(input.value) !== String(qty)) {
      input.value = qty;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (qty > 0) {
      if (addBtn) addBtn.setAttribute('hidden', '');
      if (stepper) stepper.removeAttribute('hidden');
    } else {
      if (stepper) stepper.setAttribute('hidden', '');
      if (addBtn) {
        addBtn.removeAttribute('hidden');
        // Clear a disabled state left behind by an interrupted request, which
        // would otherwise leave the button dead and unrepairable.
        if (!addBtn.classList.contains('mnm-card__quickadd--soldout')) {
          addBtn.removeAttribute('disabled');
        }
        repairButton(addBtn);
      }
    }
  }

  function sync() {
    if (inFlight) {
      queued = true;
      return inFlight;
    }

    inFlight = fetch(cartUrl(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart.js responded ' + res.status);
        return res.json();
      })
      .then(function (cart) {
        var quantities = quantitiesFromCart(cart);
        var controls = document.querySelectorAll(CONTROL);
        for (var i = 0; i < controls.length; i++) {
          var id = String(controls[i].getAttribute('data-variant-id'));
          applyToCard(controls[i], quantities[id] || 0);
        }
        watchAllButtons();
      })
      .catch(function (err) {
        // A failed sync leaves cards as they are. Better a stale count than
        // wrongly resetting a stepper the shopper just set.
        console.warn('[mnm-card] cart sync failed:', err);
      })
      .then(function () {
        inFlight = null;
        if (queued) {
          queued = false;
          sync();
        }
      });

    return inFlight;
  }

  var debounceTimer = null;
  function syncSoon(delay) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(sync, delay == null ? 120 : delay);
  }

  /* --- Triggers ----------------------------------------------------------- */

  // Theme cart events. Names vary between themes and versions, so listen
  // broadly — a redundant sync is one cheap request.
  ['cart:update', 'cart:refresh', 'cart:change', 'cart-update', 'variant:update'].forEach(
    function (name) {
      document.addEventListener(name, function () {
        syncSoon();
      });
    }
  );

  // Cart drawer closing. Dialog-based drawers fire 'close'; others toggle an
  // open / aria-hidden attribute, so watch for that too.
  document.addEventListener(
    'close',
    function (event) {
      var el = event.target;
      if (
        el &&
        el.nodeType === 1 &&
        /cart|drawer/i.test(el.tagName + ' ' + (el.id || '') + ' ' + (el.className || ''))
      ) {
        syncSoon();
      }
    },
    true
  );

  if ('MutationObserver' in window) {
    var drawerObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var target = mutations[i].target;
        if (target.nodeType !== 1) continue;

        var label =
          target.tagName +
          ' ' +
          (target.id || '') +
          ' ' +
          (typeof target.className === 'string' ? target.className : '');
        if (!/cart|drawer/i.test(label)) continue;

        var name = mutations[i].attributeName;
        var closedNow =
          (name === 'open' && !target.hasAttribute('open')) ||
          (name === 'aria-hidden' && target.getAttribute('aria-hidden') === 'true') ||
          (name === 'aria-expanded' && target.getAttribute('aria-expanded') === 'false') ||
          (name === 'hidden' && target.hasAttribute('hidden'));

        if (closedNow) {
          syncSoon(200); // let the drawer finish its own cart request first
          return;
        }
      }
    });

    drawerObserver.observe(document.documentElement, {
      subtree: true,
      attributes: true,
      attributeFilter: ['open', 'aria-hidden', 'aria-expanded', 'hidden', 'class']
    });
  }

  // Returning to the tab, and back/forward navigation restoring a cached page.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') syncSoon(0);
  });
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) syncSoon(0);
  });
  window.addEventListener('focus', function () {
    syncSoon(300);
  });

  function start() {
    watchAllButtons();
    syncSoon(0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Exposed for other scripts, and for forcing a sync from the console.
  window.mnmCardCartSync = sync;
})();